import React, { useState } from "react";
import { FileText, Download, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Card } from "@/components/ModulePage";
import StatusBadge from "@/components/StatusBadge";

const REPORTS = [
  { key: "dashboard", name: "Executive Report", endpoint: "/dashboard" },
  { key: "security-tests", name: "AI Security Assessment", endpoint: "/security-tests" },
  { key: "red-team", name: "Red Team Report", endpoint: "/red-team" },
  { key: "risk-register", name: "AI Risk Assessment", endpoint: "/risk-register" },
  { key: "audit-controls", name: "Compliance Evidence Pack", endpoint: "/audit-controls" },
  { key: "maturity", name: "Maturity Report", endpoint: "/maturity" },
  { key: "business-value", name: "Business Value Report", endpoint: "/business-value" },
  { key: "soc-alerts", name: "SOC Report", endpoint: "/soc-alerts" },
];

export default function Reports() {
  const [busy, setBusy] = useState("");

  const generate = async (r, fmt) => {
    setBusy(r.key + fmt);
    try {
      const { data } = await api.get(r.endpoint);
      const payload = { report: r.name, environment: "DEMO / SIMULATION", generated_at: new Date().toISOString(), disclaimer: "Synthetic demo data. Not a compliance certification.", data };
      let blob, filename;
      if (fmt === "json") { blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" }); filename = `${r.key}.json`; }
      else {
        const arr = Array.isArray(data) ? data : [data];
        const keys = Object.keys(arr[0] || {});
        const csv = [keys.join(","), ...arr.map((row) => keys.map((k) => JSON.stringify(row[k] ?? "")).join(","))].join("\n");
        blob = new Blob([csv], { type: "text/csv" }); filename = `${r.key}.csv`;
      }
      const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
    } catch {} finally { setBusy(""); }
  };

  return (
    <div>
      <PageHeader title="Reports" subtitle="Generate assessment, assurance and executive reports. Every report is clearly labelled DEMO / SIMULATION." readAloudContext="Reports center. Generate executive, security, red team, risk, compliance evidence, maturity, business value and SOC reports in JSON or CSV. All reports carry a demo and simulation label." />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {REPORTS.map((r) => (
          <Card key={r.key} testId={`report-${r.key}`}>
            <div className="mb-3 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/20"><FileText className="h-5 w-5 text-accent" /></div>
              <div><div className="text-sm font-semibold">{r.name}</div><StatusBadge value="DEMO" /></div>
            </div>
            <div className="flex gap-2">
              <button data-testid={`${r.key}-json`} onClick={() => generate(r, "json")} disabled={busy} className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-border px-3 py-2 text-xs font-semibold hover:bg-secondary disabled:opacity-50">{busy === r.key + "json" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} JSON</button>
              <button data-testid={`${r.key}-csv`} onClick={() => generate(r, "csv")} disabled={busy} className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-border px-3 py-2 text-xs font-semibold hover:bg-secondary disabled:opacity-50">{busy === r.key + "csv" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} CSV</button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
